import React, { useState, useEffect, useRef } from 'react';

import { IProductsTableSearch } from './interface';

import { eventName, produtosAction } from 'components/ApplicationInsights/eventNames';
import { Row, Column } from 'components/Layout/Grid';
import Text from 'components/Text';
import { Button } from 'components/Button';
import { ISubCategory } from 'models/Product';
import Form from 'components/Form';

import IconSearch from 'img/search-icon.svg';

import {
  addKeyPressListener,
  removeKeyPressListener,
} from 'utils/searchWithEnterPress';

const ProductsTableSearch: React.FC<IProductsTableSearch> = (props) => {
  const {
    setTableFilter,
    setCurrentPage,
    setShowTable,
    categoryFilters,
    selectedCategory,
    selectedSubCategory,
    className,
  } = props;

  const randomTableSearchId = `${Math.random() * 1000}`;

  const [subCategoryFilters, setSubCategoryFilters] = useState(
    Array<ISubCategory>()
  );

  const [productSearchStr, setProductSearchStr] = useState('');
  const [codeSearchStr, setCodeSearchStr] = useState('');
  const [supplierSearchStr, setSupplierSearchStr] = useState('');
  const [categorySelectedOption, setCategorySelectedOption] = useState('');
  const [subCategorySelectedOption, setSubCategorySelectedOption] = useState(
    ''
  );
  const productSearchStrRef = useRef(productSearchStr);
  const codeSearchStrRef = useRef(codeSearchStr);
  const supplierSearchStrRef = useRef(supplierSearchStr);
  const categorySelectedOptionRef = useRef(categorySelectedOption);
  const subCategorySelectedOptionRef = useRef(subCategorySelectedOption);

  function findButtonClick() {
    makeFilter();
    setShowTable(true);
  }

  function makeFilter(
    _selectedCategory?: string,
    _selectedSubCategory?: string
  ) {
    const productSearchStrCurrent = productSearchStrRef.current;
    const codeSearchStrCurrent = codeSearchStrRef.current;
    const supplierSearchStrCurrent = supplierSearchStrRef.current;
    const categorySelectedOptionCurrent = categorySelectedOptionRef.current;
    const subCategorySelectedOptionCurrent = subCategorySelectedOptionRef.current;

    const filterData: any = {
      and: {},
    };

    if (codeSearchStrCurrent) {
      filterData.and = {
        ...filterData.and,
        'tolower(code)': { eq: codeSearchStrCurrent.toLowerCase() },
      };
    }

    if (productSearchStrCurrent) {
      filterData.and = {
        ...filterData.and,
        or: [
          { 'tolower(name)': { contains: productSearchStrCurrent.toLowerCase() } },
          {
            'tolower(substance)': { contains: productSearchStrCurrent.toLowerCase() },
          },
        ],
      };
    }

    if (supplierSearchStrCurrent) {
      filterData.and = {
        ...filterData.and,
        'tolower(supplier)': { contains: supplierSearchStrCurrent.toLowerCase() },
      };
    }

    /**
     * Category/Sub option format
     */
    if (categorySelectedOptionCurrent || _selectedCategory) {
      filterData.and = {
        ...filterData.and,
        category: { eq: _selectedCategory || categorySelectedOptionCurrent },
      };
    }
    if (subCategorySelectedOptionCurrent || _selectedSubCategory) {
      filterData.and = {
        ...filterData.and,
        category: { eq: _selectedSubCategory || subCategorySelectedOptionCurrent },
      };
    }

    setTableFilter((filters: any) => ({ ...filters, ...filterData }));
    setCurrentPage(0);
  }

  function OnChangeCategory(categoryKey: string) {
    setCategorySelectedOption(categoryKey);
    const subOptions =
      categoryFilters?.find((c) => c._mockKey === categoryKey)?.subOptions ||
      Array<ISubCategory>();
    setSubCategoryFilters(subOptions);
  }

  useEffect(() => {
    if (selectedSubCategory) {
      OnChangeCategory(selectedCategory);
      setSubCategorySelectedOption(selectedSubCategory);
    } else if (selectedCategory) {
      OnChangeCategory(selectedCategory);
    }
    makeFilter(selectedCategory, selectedSubCategory);
  }, [selectedCategory, selectedSubCategory]);

  useEffect(() => {
    productSearchStrRef.current = productSearchStr;
  }, [productSearchStr]);

  useEffect(() => {
    codeSearchStrRef.current = codeSearchStr;
  }, [codeSearchStr]);

  useEffect(() => {
    supplierSearchStrRef.current = supplierSearchStr;
  }, [supplierSearchStr]);

  useEffect(() => {
    subCategorySelectedOptionRef.current = subCategorySelectedOption;
  }, [subCategorySelectedOption]);

  useEffect(() => {
    codeSearchStrRef.current = codeSearchStr;
  }, [codeSearchStr]);

  useEffect(() => {
    addKeyPressListener(randomTableSearchId, findButtonClick);

    return () => {
      removeKeyPressListener(randomTableSearchId, findButtonClick);
    };
  }, []);

  const OptionCategory = (filters: any) => (
    filters.map((filter: any, index: any) => (
      <option
        key={index}
        label={filter.name}
        value={filter._mockKey}
      />
    ))
  );

  return (
    <Form id="table-search-products" onValidate={findButtonClick}>
      <Row
        id={randomTableSearchId}
        className={`mt-4 mt-md-2 align-center flex-md-column align-md-none flex-wrap justify-md-none bg-white ${className || 'px-3 py-3'
          }`}
        style={{ borderRadius: '10px 10px 0px 0px' }}
      >
        <Column className="mr-2 mr-md-2 mb-md-3 flex-2 w-md-15">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Código
          </Text>
          <Row className="flex-1">
            <input
              id="code-input"
              value={codeSearchStr}
              onChange={(e) => setCodeSearchStr(e.target.value)}
              type="text"
              className="flex-1"
              placeholder="Código"
            />
          </Row>
        </Column>
        <Column className="mr-2 mr-md-2 mb-md-3 flex-3 w-md-100 w-20">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Produto ou substância
          </Text>
          <Row className="flex-1">
            <input
              id="product-substance"
              value={productSearchStr}
              onChange={(e) => setProductSearchStr(e.target.value)}
              type="text"
              className="flex-1"
              placeholder="Produto ou substância"
            />
          </Row>
        </Column>
        {categoryFilters && (
          <>
            <Column className="mr-2 mr-lg-0 mt-md-3 flex-2 w-md-100 w-20">
              <Text
                className="mr-2 mb-1 color-blue-gray"
                size={12}
                weight="bold"
              >
                Categoria
              </Text>
              <Row className="flex-1">
                <select
                  value={categorySelectedOption}
                  onChange={(e) => OnChangeCategory(e.target.value)}
                  className="flex-1"
                >
                  <option value="">Selecione</option>
                  <OptionCategory filter={categoryFilters} />
                </select>
              </Row>
            </Column>
            <Column className="mr-2 mr-lg-0 mt-md-3 flex-2 w-md-100 w-20">
              <Text
                className="mr-2 mb-1 color-blue-gray"
                size={12}
                weight="bold"
              >
                Sub-Categoria
              </Text>
              <Row className="flex-1">
                <select
                  value={subCategorySelectedOption}
                  onChange={(e) => setSubCategorySelectedOption(e.target.value)}
                  className="flex-1"
                >
                  <option value="">Selecione</option>
                  <OptionCategory filter={subCategoryFilters} />
                </select>
              </Row>
            </Column>
          </>
        )}
        <Column className="mr-2 mr-md-2 mb-md-3 flex-3 w-md-100 w-20">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Fornecedor
          </Text>
          <Row className="flex-1">
            <input
              value={supplierSearchStr}
              onChange={(e) => setSupplierSearchStr(e.target.value)}
              type="text"
              className="flex-1"
              placeholder="Fornecedor"
            />
          </Row>
        </Column>
        <Column className="mr-2 mr-lg-0 mb-md-2 flex-1 w-md-100 ">
          <Text className="mr-2 mb-1 color-white" size={12}>
            .
          </Text>
          <Row>
            <Button
              id="btn-search-product"
              width="auto"
              className="focus-button"
              onClick={findButtonClick}
              trackAction={eventName.PRODUTOS}
              trackName={produtosAction.BUSCAPRODUTO}
            >
              <img src={IconSearch} alt="" />
            </Button>
          </Row>
        </Column>
      </Row>
    </Form>
  );
};

export default ProductsTableSearch;
